import { FunctionComponent, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { Store } from 'redux';
import { getHistory } from '@/utils/history';
import { initialShowExtendedSegmentation } from '@/utils/segmentationTestHelper';
import { DeliveryChristmasHint } from '@/components/common/DeliveryChristmasHint';
import { DesignerHeader } from '@/components/common/DesignerHeader';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { ModalManager } from '@/components/common/ModalManager';
import { OutOfStockMessage } from '@/components/common/OutOfStockMessage';
import { ProductDetailsPanel } from '@/components/common/ProductDetailsPanel';
import { ProductViewSelector } from '@/components/common/ProductViewSelector';
import { SegmentationTest } from '@/components/common/SegmentationTestModal/SegmentationTestModal';
import { WarningList } from '@/components/common/WarningList';
import { ZeroStatePanel } from '@/components/common/ZeroStatePanel';
import { AsyncCheckoutSuccess } from '@/components/common/checkoutDialog/AsyncCheckoutSuccess';
import { DesignSelector } from '@/components/common/design-selector/DesignSelector';
import { FocusProvider } from '@/components/common/focus';
import { Memento } from '@/components/common/memento/Memento';
import { AsyncProductTypeSelector } from '@/components/common/product-type-selector/AsyncProductTypeSelector';
import { Sidebar } from '@/components/common/sidebar/Sidebar';
import { AsyncUserDraftsDialog } from '@/components/common/user-drafts/AsyncUserDraftsDialog';
import { ConfigurationError } from '@/components/desktop/ConfigurationError';
import { DesktopMenu } from '@/components/desktop/DesktopMenu';
import { PanelContainer } from '@/components/desktop/PanelContainer';
import { AsyncUploadPanel } from '@/components/desktop/upload-panel/AsyncUploadPanel';
import { DraftViewerRedux } from '@/components/draft/DraftViewerRedux';
import './Sketchomat.scss';

const Sketchomat: FunctionComponent = () => {
  const [showExtendedSegmentation, setShowExtendedSegmentation] = useState(
    initialShowExtendedSegmentation()
  );

  return (
    <div className="designer">
      <DesignerHeader />
      <DesktopMenu />

      <DraftViewerRedux />

      <div className="product-view-selector-container">
        <ProductViewSelector />
      </div>

      <AsyncCheckoutSuccess />
      <PanelContainer />
      <Memento />
      <Sidebar />
      <AsyncUserDraftsDialog />
      <AsyncProductTypeSelector />
      <DesignSelector />
      <AsyncUploadPanel />
      {showExtendedSegmentation ? (
        <SegmentationTest
          onClose={() => setShowExtendedSegmentation(initialShowExtendedSegmentation(false))}
        />
      ) : (
        <ZeroStatePanel />
      )}
      <ModalManager />
      <ProductDetailsPanel />

      <div className="message-container">
        <OutOfStockMessage />
        <ConfigurationError />
        <WarningList />
        <DeliveryChristmasHint />
      </div>
      <div id="dnd-container" />
    </div>
  );
};

const renderSketchomat = (store: Store, target: HTMLElement) => {
  const root = createRoot(target);
  root.render(
    <ErrorBoundary>
      <FocusProvider>
        <Provider store={store}>
          <HistoryRouter history={getHistory()} basename="/designers/sketchomat">
            <Sketchomat />
          </HistoryRouter>
        </Provider>
      </FocusProvider>
    </ErrorBoundary>
  );
};

// dynamic imports should be default exports
// eslint-disable-next-line import/no-default-export
export default renderSketchomat;
